/***************************************************************************

    Copyright (C) 2020 Ravenpod, Inc.
    All rights reserved.

    This material contains CONFIDENTIAL INFORMATION
    that is the property of Ravenpod.com, Inc.
    Any unauthorized use, duplication or disclosure is prohibited.

***************************************************************************/
import axios from 'axios'
import urlTemplate from 'url-template';
import authHeader from './auth-header';
import store from '../store';

const API_URI = process.env.VUE_APP_RAVENPOD_API_URI

const API = axios.create()

API.interceptors.request.use(function (config) {
    config.headers = authHeader()
    return config;
});

API.interceptors.response.use(function (response) {
    return response
}, function (error) {
    console.log("Error in API response", error)
    if (error.response.status === 401) {
        console.log("Token invalid. Loggout out the user.")
        store.dispatch('auth/logout')
    }
    return Promise.reject(error)
})

const user = {
    getSubscriptionStatus: params => API.get(`${API_URI || '' }/api/user/getSubscriptionStatus`, { params }), 
}

const bizTxn = {
    getMilestonesByAssetKeyAndTxnDef: params => API.get(`${API_URI || '' }/api/biz_txn/getMilestonesByAssetKeyAndTxnDef`, { params }), 
    getVitalByMSPAndURIs: params => API.get(`${API_URI || '' }/api/biz_txn/getVitalByMSPAndURIs`, { params }), 
    getTxnDefsByChannelName: params => API.get(`${API_URI || '' }/api/biz_txn/getTxnDefsByChannelName`, { params }),
    create: params => API.post(`${API_URI || '' }/api/biz_txn/`, params), 
}

const assetComments = {
    getComments: params => API.get(`${API_URI || '' }/api/asset_comments`, { params }), 
    postComment: params => API.post(`${API_URI || '' }/api/asset_comments`, params), 
}

const assets = {
    getAssetList: params => API.get(`${API_URI || '' }/api/assets/list`, { params }), 
    getAssetOperationHistory: params => API.get(`${API_URI || '' }/api/assets/operationHistory`, { params }), 
    getAssetURIHistory: params => API.get(`${API_URI || '' }/api/assets/uriHistory`, { params }),
}

const userApps = {
    getWebAppList: params => API.get(`${API_URI || '' }/api/user_apps/list`, { params }), 
}

const webRequests = {
    getVitalData: params => API.get(`${API_URI || '' }/api/web/vital`, { params }),
    getTransactionList: params => API.get(`${API_URI || '' }/api/web/list`, { params }),
    getVitalDataByURI: params => API.get(`${API_URI || '' }/api/web/vitalDataByURI`, { params }),
    getVitalDataByWebTxnId: params => API.get(`${API_URI || '' }/api/web/vitalDataByWebTxnId`, { params }),
}

const blockchainRequests = {
    getVitalData: params => API.get(`${API_URI || '' }/api/blockchain/vital`, { params }),
    getTopologyByRavenpodTxnId: (params, urlParams) => {
        const url = urlTemplate.parse(`${API_URI || '' }/api/blockchain/{ravenpod_txn_id}/topology`);
        const finalUrl = url.expand(urlParams);
        return API.get(finalUrl, { params })
    },
    getEndorsementResults: (params, urlParams) => {
        const url = urlTemplate.parse(`${API_URI || '' }/api/blockchain/{ravenpod_txn_id}/endorsement_results`);
        const finalUrl = url.expand(urlParams);
        return API.get(finalUrl, { params })
    },
    getBlockchainRequestListByWebTxnId: params => API.get(`${API_URI || '' }/api/blockchain/list`, { params }),
    getVitalDataByChaincodeFunc: params => API.get(`${API_URI || '' }/api/blockchain/vital_by_chaincode_func`, { params }),
    getInvocationStepsByRavenpodTxnId: (params, urlParams) => {
        const url = urlTemplate.parse(`${API_URI || '' }/api/blockchain/{ravenpod_txn_id}/invocation_steps`);
        const finalUrl = url.expand(urlParams);
        return API.get(finalUrl, { params })
    },
    getTrace: (params, urlParams) => {
        const url = urlTemplate.parse(`${API_URI || '' }/api/blockchain/{ravenpod_txn_id}/trace`);
        const finalUrl = url.expand(urlParams);
        return API.get(finalUrl, { params })
    },
    getSlowestFunctions: (params, urlParams) => {
        const url = urlTemplate.parse(`${API_URI || '' }/api/blockchain/{ravenpod_txn_id}/slowest_functions`);
        const finalUrl = url.expand(urlParams);
        return API.get(finalUrl, { params })
    },
}

const channels = {    
    getTopology: params => API.get(`${API_URI || '' }/api/channels/topology`, { params }),
    getVitalDataByChannel: params => API.get(`${API_URI || '' }/api/channels/vitalDataByChannel`, { params }),
    getChannelList: params => API.get(`${API_URI || '' }/api/channels`, { params }),
    getInvocationListByChannel: params => API.get(`${API_URI || '' }/api/channels/invocationListByChannel`, { params }),
    getInvocationListByChannelAndChaincode: params => API.get(`${API_URI || '' }/api/channels/invocationListByChannelAndChaincode`, { params }),
    getVitalDataByChannelAndChaincodeNameAndFunc: params => API.get(`${API_URI || '' }/api/channels/vitalDataByChannelAndChaincodeNamdAndFunc`, { params }),
    getVitalDataByChannelAndHostnameAndStage: params => API.get(`${API_URI || '' }/api/channels/vitalDataByChannelAndHostnameAndStage`, { params }),
    getVitalDataByChannelAndHostnameAndStageAndChaincodeName: params => API.get(`${API_URI || '' }/api/channels/VitalDataByChannelAndHostnameAndStageAndChaincodeName`, { params }),
    getVitalDataByChannelAndHostnameAndStageGroupByChaincode: params => API.get(`${API_URI || '' }/api/channels/vitalDataByChannelAndHostnameAndStageGroupByChaincode`, { params }),
}

export default {
    userApps,
    webRequests,
    blockchainRequests,
    channels,
    assets,
    assetComments,
    bizTxn,
    user
}
 